import { useFragment } from '@apollo/client';
import { useMemo } from 'react';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { gql } from 'src/gql';
import {
  Address,
  Conversation,
  isClientDeleted,
  mapClient,
  useAddress,
  useDeleteClient,
  useLabelToCta,
  useRestoreClient,
} from 'src/graphql';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { EntityNames } from 'src/graphql/types';
import { Modals, useModalMethods } from 'src/store/modals';
import type { ClientRaw } from 'src/types';
import {
  useConfirmationModal,
  useCopyToClipboard,
  useEntityFullUrl,
  useGetTosUrl,
} from 'src/utils/hooks';

const LUCIDE_ICON_LIBRARY: IconLibrary = 'lucide';

const UseClientActionsFragment = gql(`
  fragment UseClientActions_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    isDeleted @client
    deletedAt
    notes
    marketingOptOut
    primaryEmail {
      id
      value
      labelId
    }
    primaryPhoneNumber {
      id
      value
      labelId
    }
    emails {
      id
      clientEmailId
      value
      labelId
    }
    phoneNumbers {
      id
      clientPhoneNumberId
      value
      labelId
    }
  }
`);

export const useClientActions = ({
  clientId,
  address,
  conversation,
}: {
  clientId: string;
  address?: Partial<Address>;
  conversation?: Conversation;
}) => {
  const { data: client } = useFragment({
    fragment: UseClientActionsFragment,
    fragmentName: 'UseClientActions_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const { tosUrl } = useGetTosUrl({ partnerId: client.partnerId, brandId: address?.brandId });
  const { confirm } = useConfirmationModal();
  const { updateAddress, deleteClientToAddress } = useAddress(address?.addressId);
  const { deleteLabelToCta } = useLabelToCta();
  const { deleteClient } = useDeleteClient();
  const { restoreClient } = useRestoreClient();
  const { groupRules } = useGroupRules();
  const { copyToClipboard } = useCopyToClipboard();
  const { showModal: showAddClientModal } = useModalMethods(Modals.AddClientModal);
  const { showModal: showMergeClientModal } = useModalMethods(Modals.MergeClientModal);

  const clientFullUrl = useEntityFullUrl(client.partnerId, EntityNames.Client, clientId);

  // TODO(gql-migrate): AddClientModal and MergeClientModal still consume the legacy mapped
  // Client shape; drop this bridge once they read the cache via useFragment themselves.
  const legacyClient = useMemo(() => mapClient(client as ClientRaw), [client]);

  return useMemo(
    () => ({
      updateClient: {
        label: 'Update client',
        testId: 'client-menu-update-client',
        icon: 'pencil',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !isClientDeleted(client),
        handler: () => {
          showAddClientModal({ client: legacyClient, address });
        },
      },
      copyClientId: {
        label: 'Copy Client ID',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: async () => {
          await copyToClipboard(clientId);
        },
      },
      copyClientUrl: {
        label: 'Copy Client URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!clientFullUrl,
        handler: async () => {
          await copyToClipboard(clientFullUrl);
        },
      },
      copyTOS: {
        label: 'Copy Terms of Service URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!tosUrl,
        handler: async () => {
          await copyToClipboard(tosUrl);
        },
      },
      disassociateClient: {
        label: 'Disassociate Client',
        icon: 'unlink',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!address?.addressId,
        handler: async () => {
          if (
            await confirm({
              title: 'Disassociate client?',
              body: 'The client is no longer linked to this site.',
              confirmLabel: 'Disassociate client',
              danger: true,
            })
          ) {
            if (address?.primaryClientId === clientId) {
              await updateAddress(
                {
                  ...address,
                  primaryClientId: null,
                  primaryClient: {
                    clientId: null,
                  },
                },
                conversation?.conversationId,
              );
            }
            await deleteLabelToCta({
              clientId,
              addressId: address?.addressId,
            });
            await deleteClientToAddress(
              clientId,
              address?.partner?.partnerId ?? address?.partnerId,
            );
          }
        },
      },
      mergeClients: {
        label: 'Merge clients',
        testId: 'client-menu-merge',
        icon: 'merge',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !isClientDeleted(client),
        handler: () => {
          showMergeClientModal({ client: legacyClient });
        },
      },
      deleteClient: {
        label: 'Delete client',
        icon: 'trash-2',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          !!groupRules[UserGroupRules.AccessToDeleteClients] && !isClientDeleted(client),
        handler: async () => {
          if (
            await confirm({
              title: 'Delete client?',
              body: 'The client and their contact details are removed, and you can restore them later.',
              confirmLabel: 'Delete client',
              danger: true,
            })
          ) {
            await deleteClient(clientId);
          }
        },
      },
      restoreClient: {
        label: 'Restore client',
        icon: 'rotate-ccw',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          isClientDeleted(client) && !!groupRules[UserGroupRules.AccessToRestoreClients],
        handler: async () => {
          await restoreClient(clientId);
        },
      },
    }),
    [
      clientFullUrl,
      tosUrl,
      address,
      showAddClientModal,
      client,
      clientId,
      legacyClient,
      copyToClipboard,
      confirm,
      deleteLabelToCta,
      deleteClientToAddress,
      updateAddress,
      conversation?.conversationId,
      showMergeClientModal,
      deleteClient,
      restoreClient,
      groupRules,
    ],
  );
};
